import React, {
  ComponentPropsWithoutRef,
  ElementType,
  useCallback,
} from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import Input from '../controls/input/Input';
import Label from '../controls/label/Label';

import './Clearance.scss';

export enum ClearanceType {
  weight = 'weight',
  height = 'height',
  length = 'length',
  width = 'width',
}

type ClearanceFieldKey = 'tons' | 'feet' | 'inches';

interface ClearanceField {
  key: ClearanceFieldKey;
  label: string;
  integer?: boolean;
}

const clearanceFields: Record<ClearanceType, ClearanceField[]> = {
  [ClearanceType.weight]: [
    {
      key: 'tons',
      label: 'American Tons',
    },
  ],
  [ClearanceType.height]: [
    {
      key: 'feet',
      label: 'Feet',
      integer: true,
    },
    {
      key: 'inches',
      label: 'Inches',
    },
  ],
  [ClearanceType.length]: [
    {
      key: 'feet',
      label: 'Feet',
      integer: true,
    },
    {
      key: 'inches',
      label: 'Inches',
    },
  ],
  [ClearanceType.width]: [
    {
      key: 'feet',
      label: 'Feet',
      integer: true,
    },
    {
      key: 'inches',
      label: 'Inches',
    },
  ],
};

export interface ClearanceProps extends ComponentPropsWithoutRef<ElementType> {
  className?: string;
  type: ClearanceType;
  value: Record<ClearanceFieldKey, number>;
  onChange?;
}

const Clearance = ({ className, type, value, onChange }: ClearanceProps) => {
  const handleInputChange = useCallback(
    (event) => {
      const inputValue = event.target.value;
      const fieldKey = event.target.getAttribute('fieldKey');
      const field = clearanceFields[type].find(
        (field) => field.key === fieldKey,
      );

      if (!field) {
        return;
      }

      if (field.integer && !/^\d*$/.test(inputValue)) {
        return;
      }

      let newValue: number | null = null;

      if (inputValue) {
        newValue = field.integer
          ? Number.parseInt(inputValue, 10)
          : Number.parseFloat(inputValue);
      }

      onChange({
        ...value,
        [fieldKey]: newValue,
      });
    },
    [onChange, value, type],
  );

  return (
    <div className={classnames('clearance', className)}>
      {clearanceFields[type]?.map((field) => (
        <div
          key={field.key}
          className={`clearance__field clearance__field-${type} clearance__field clearance__field-${type}-${field.key}`}
        >
          <Label className="clearance__field-label" label={field.label} />
          <Input
            fieldKey={field.key}
            className="clearance__field-input"
            value={isNil(value?.[field.key]) ? '' : value[field.key]}
            type={field.integer ? 'text' : 'number'}
            pattern={field.integer ? '\\d*' : undefined}
            onChange={handleInputChange}
            // prevents changing numeric input value on mouse wheel event
            onWheel={(e) => e.target.blur()}
          />
        </div>
      ))}
    </div>
  );
};

export default Clearance;
